/* istanbul ignore file */
import React from 'react'
import PropTypes from 'prop-types'
import { Control, Errors, actionTypes } from 'commons/js/components/form'
import Date, { internalFormat } from '../Date'
import { required as requiredValidator, isValidDate } from 'commons/js/util/validators'
import { injectIntl } from 'react-intl'
import messages from './messages'
import moment from 'moment'
import { ErrorsWrapper, FieldError } from '@elparking/components'

const parseValue = ({ fieldValue }) => {
    const newDate = moment(fieldValue.value, internalFormat, true)
    const value = newDate.isValid()
        ? {
            DD: newDate.format('D'),
            MM: newDate.format('M'),
            YYYY: newDate.format('YYYY'),
        }
        : {
            DD: undefined,
            MM: undefined,
            YYYY: undefined,
        }
    return value
}

const mapProps = {
    valid: ({ fieldValue: field }) => !field.touched || (!field.validating && field.valid),
    changed: ({ fieldValue: field }) => field.touched,
    customChange: ({change}) => change,
    errors: ({ fieldValue: field }) => field.errors,
    forceFocus: ({ fieldValue }) => {
        const { intents } = fieldValue
        if (!Array.isArray(intents) || !intents.length) {
            return false
        }
        return intents.some((intent) => intent.type === actionTypes.FOCUS)
    },
    defaultValue: parseValue,
    value: parseValue,
    onChange: ({ onChange }) => (value) => {
        if (!value || !value.YYYY || !value.MM || !value.DD) {
            return onChange(undefined)
        }
        return onChange(moment(`${value.YYYY}-${value.MM}-${value.DD}`, internalFormat, true).format(internalFormat))
    },
}

const DateField = React.forwardRef(({
    element: Element,
    model,
    errorMessages,
    showErrorOptions,
    errorColor,
    autoComplete,
    dataTest,
    inputElement,
    intl,
    required,
    validators,
    label,
    ...restProps
}, ref) => {
    return (
        <div>
            <Element
              ref={ref}
              autoComplete={autoComplete}
              component={inputElement}
              model={model}
              mapProps={mapProps}
              errorColor={errorColor}
              label={label}
              dataTest={dataTest}
              validators={{
                ...(required && { required: requiredValidator() }),
                validDate: isValidDate(internalFormat),
                ...validators,
              }}
              {...restProps}
            />
            <Errors
              wrapper={ErrorsWrapper}
              component={({children, fieldValue}) => <FieldError errors={fieldValue.errors} errorColor={errorColor}>{children}</FieldError>}
              model={model}
              show={showErrorOptions}
              messages={{
                    ...(required && { required: intl.formatMessage(messages.requiredError) }),
                    validDate: intl.formatMessage(messages.validDate),
                    underAge: intl.formatMessage(messages.underAge),
                    ...errorMessages,
                }}
            />
        </div>
    )
})

DateField.propTypes = {
    model: PropTypes.string.isRequired,
    intl: PropTypes.object,
    validators: PropTypes.object,
    element: PropTypes.func,
    showErrorOptions: PropTypes.any,
    errorMessages: PropTypes.object,
    errorColor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    autoComplete: PropTypes.string,
    dataTest: PropTypes.string,
    inputElement: PropTypes.func,
    required: PropTypes.bool,
    label: PropTypes.node,
}

DateField.defaultProps = {
    required: false,
    element: Control.custom,
    errorColor: 'red',
    autoComplete: 'off',
    model: '.date',
    showErrorOptions: {touched: true},
    inputElement: Date,
}
export default injectIntl(DateField)
